import { html, raw } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';

export function CssPage(): Node {
  return html`
    <div>
      <h1>Scoped CSS (css)</h1>
      <p>The <code>css</code> tagged template creates scoped stylesheets. Selectors are automatically prefixed with a unique class so styles never leak to other components.</p>

      <h2>Basic Usage</h2>
      ${CodeBlock(`import { css, html } from 'onefold';

const styles = css\`
  .card {
    background: white;
    border-radius: 8px;
    padding: 16px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
  .title {
    font-size: 18px;
    font-weight: bold;
  }
\`;

function Card(): Node {
  return html\`<div class=\${styles.scope}>
    <div class="card">
      <h2 class="title">Scoped!</h2>
      <p>These styles won't affect other components.</p>
    </div>
  </div>\`;
}`)}

      <p>The <code>styles.scope</code> property is a generated class name (e.g. <code>nf-0</code>) that gets prepended to every selector in your CSS block.</p>

      <h2>How It Works</h2>
      <p>At runtime:</p>
      <ol>
        <li>A unique class name is generated (<code>nf-0</code>, <code>nf-1</code>, ...)</li>
        <li>Every selector in your CSS is prefixed with <code>.nf-0</code></li>
        ${raw('<li>A <code>&lt;style&gt;</code> element is injected into <code>&lt;head&gt;</code> (deduplicated)</li>')}
        <li>You apply the scope class to your component's root element</li>
      </ol>

      ${Callout('Styles are deduplicated — calling css with the same template string reuses the same scope class and does not inject a second <style> element.')}

      <h2>cssValue — Safe User Input</h2>
      <p>When interpolating user-provided values into CSS, use <code>cssValue()</code> to prevent injection:</p>
      ${CodeBlock(`import { css, cssValue } from 'onefold';

const userColor = 'red; background: url(evil)';
css\`.card { background: \${cssValue(userColor)}; }\`
// Only "red" is applied — injection is stripped`)}

      ${raw('<p><code>cssValue()</code> strips <code>{ } &lt; &gt; ;</code>, blocks <code>url()</code> and <code>expression()</code>, and removes <code>@import</code>.</p>')}

      <h2>API</h2>
      <table>
        <tr><th>Function</th><th>Returns</th><th>Description</th></tr>
        <tr><td><code>css\`...\`</code></td><td>ScopedStyle</td><td>Create scoped stylesheet, inject into head</td></tr>
        <tr><td><code>styles.scope</code></td><td>string</td><td>Class name to apply to root element</td></tr>
        <tr><td><code>styles.css</code></td><td>string</td><td>Generated CSS text (for SSR/inspection)</td></tr>
        <tr><td><code>cssValue(str)</code></td><td>string</td><td>Sanitize user input for CSS interpolation</td></tr>
      </table>
    </div>
  `;
}
